import axios from 'axios';
import { PaymentsList } from 'types/Billing';
import { Company } from 'types/Company';
import { Profile } from 'types/User';
import { objectKeysToCamelCase, objectKeysToSnakeCase } from 'utils/common';
import erlClient from '.';
import axiosWithToken from './axios-with-token';

export const enum UserQueryKeys {
  TeamMembers = 'teamMembers',
}

export const signUp = async (form: {
  email: string;
  password: string;
  password_confirmation: string;
  company_name: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  terms_of_uses: boolean;
  privacy_policy: boolean;
  company: Pick<Company, 'name' | 'scac' | 'address'> | null;
  members: never[];
}) => {
  try {
    const res = await erlClient.post('core/api/v1/auth/signup', form, {
      headers: { Authorization: '' },
    });
    return res.data;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const res = e.response;
      if (res?.status === 400) throw res.data;
    }
    return undefined;
  }
};

export const signIn = async (form: { email: string; password: string }) => {
  try {
    const res = await erlClient.post('core/api/v1/auth/signin', form, {
      headers: { Authorization: '' },
    });
    return res.data;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const res = e.response;
      if (res?.status === 400) throw res.data;
    }
    return undefined;
  }
};

export const resetPassword = async (form: { email: string }) => {
  try {
    const res = await erlClient.post('/core/api/v1/auth/password_reset', form, {
      headers: { Authorization: '' },
    });
    return res.data;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const res = e.response;
      if (res?.status === 400) throw res.data;
    }
    return undefined;
  }
};

export const changePassword = async (form: { password: string; token: string }) => {
  try {
    const res = await erlClient.post('/core/api/v1/auth/password_reset/confirm', form, {
      headers: { Authorization: '' },
    });
    return res.data;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const res = e.response;
      if (res?.status === 400) throw res.data;
      if (res?.status === 404) {
        const error = { other: 'This link is no longer available' };
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw error;
      }
    }
    return undefined;
  }
};

export const getProfile = async () => {
  const res = await erlClient.get('/core/api/v1/auth/profile');
  return res.data;
};

export const updateProfile = async (data: Profile) => {
  const res = await erlClient.put('/core/api/v1/auth/profile', data);
  return res.data as Profile;
};

export const getPaymentsList = async (): Promise<PaymentsList> => {
  const res = await erlClient('/core/api/v1/auth/billing/payment/list');
  return objectKeysToCamelCase(res.data);
};

export const getTeamMembers = async (signal?: AbortSignal) => {
  const res = await erlClient.get('/core/api/v1/auth/company/members', { signal });
  return res.data as Profile[];
};

/**
 * Check if an entered code is valid for the given use
 * @param values the email and code to check
 * @returns
 */
export const checkCode = async (values: { email: string; code: string }) => {
  const response = await erlClient.post('/core/api/v1/auth/email-validation/validate', values);
  return response;
};

/**
 * Send the validation email for the provided email
 * @param email the email to send the code to
 */
export const sendValidationMail = async (email: string) => {
  const response = await erlClient.post(
    '/core/api/v1/auth/email-validation/send',
    { email },
    {
      headers: { Authorization: '' },
    }
  );
  return response;
};

type AvailabilityCode =
  | 'not_available'
  | 'not_valid'
  | 'not_work_email'
  | 'available'
  | 'needs_validation'
  | 'needs_account';

/**
 * Get the email availability status
 * @param email the email to check
 * @returns an availability code
 */
export const emailAvailabilityStatus: (email: string) => Promise<AvailabilityCode> = async (
  email
) => {
  try {
    const response = await erlClient.get(`core/api/v1/auth/user/email/${email}`);
    return response.data.code as AvailabilityCode;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.response?.data?.code) {
      return error.response.data.code as AvailabilityCode;
    }
  }
  throw new Error('Could not determine availability');
};

type AutoJoinAvailable = 'auto_join_available' | 'auto_join_unavailable';

/**
 * Check if an email is a work email according to our backend
 * @param email the email to check
 * @returns a code declaring if it is or is not a valid work email
 */
export const checkIfAutoJoinAvailable: (
  email: string,
  signal?: AbortSignal
) => Promise<AutoJoinAvailable> = async (email, signal) => {
  const response = await erlClient.get<{ code: AutoJoinAvailable }>(
    `core/api/v1/auth/user/auto-join-available/${email}`,
    { signal }
  );
  return response.data.code;
};

export interface ProfilePayload {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyRole: string;
  phoneNumber: string;
}

/**
 * Create a user profile
 * @param profile
 * @returns snake case user profile object
 */
export const createProfile = async (profile: ProfilePayload) => {
  const response = await erlClient.post(
    'core/api/v1/auth/user/create',
    objectKeysToSnakeCase(profile)
  );
  return response.data;
};

type AvailableCompanyResponse = CompanyAvailable | NoCompanyAvailable;

export interface AvailableCompany {
  name: string;
  id: string;
  source?: 'domain' | 'invite';
}
interface CompanyAvailable {
  code: 'company_available';
  companies: AvailableCompany[];
}

interface NoCompanyAvailable {
  code: 'no_companies';
}

export const getAvailableCompanies: (signal?: AbortSignal) => Promise<AvailableCompany[]> = async (
  signal
) => {
  const response = await axiosWithToken.get<AvailableCompanyResponse>(
    'core/api/v1/auth/company/available',
    { signal }
  );

  switch (response.data.code) {
    case 'company_available': {
      const { companies } = response.data;
      return companies;
    }
    case 'no_companies':
    default:
      return [];
  }
};

interface JoinWithDomainJoined {
  code: 'company_joined';
}
interface JoinWithDomainNone {
  code: 'no_companies';
}
type JoinWithDomainResponse = JoinWithDomainJoined | JoinWithDomainNone;

export const joinCompany: (id: string) => Promise<JoinWithDomainResponse['code']> = async (id) => {
  const response = await axiosWithToken.post<JoinWithDomainResponse>(
    'core/api/v1/auth/company/join_domain',
    { company_id: id }
  );
  return response.data.code;
};

export const joinCompanyWithScac: (scac: string) => Promise<{ code: string }> = async (
  scac: string
) => {
  const response = await axiosWithToken.post('core/api/v1/auth/company/join', { scac });
  return response.data;
};

interface CheckScacExists {
  code: 'scac_exists';
  name: string;
}

interface CheckScacFree {
  code: 'scac_free';
}

export type CheckScacResponse = CheckScacExists | CheckScacFree;

export const checkScacFree: (scac: string) => Promise<CheckScacResponse> = async (scac) => {
  try {
    const response = await erlClient.get(`core/api/v1/auth/company/scac/${scac}`);
    return response.data;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      if (e.response?.data.code === 'scac_exists') {
        return e.response.data;
      }
    }
    throw e;
  }
};

export interface CreateCompanyPayload {
  name: string;
  scac?: string;
  auto_join_email_domain?: boolean;
  company_type_detail:
    | 'Drayage Carrier'
    | 'Owner Operator'
    | 'Broker'
    | '3PL'
    | 'Warehousing'
    | 'Freight Forwarder'
    | 'BCO / Consignee'
    | 'Other';
  port_collection_codes?: string[];
  port_codes?: string[];
  expected_container_volume: number;
  other_type?: string;
}

export const createCompany: (companyPayload: CreateCompanyPayload) => Promise<void> = async (
  companyPayload
) => {
  const response = await axiosWithToken.post('core/api/v1/auth/company/create', companyPayload);
  return response.data;
};

export const inviteTeamMembers: (inviteTeamMembers: string[]) => Promise<void> = async (emails) => {
  const response = await axiosWithToken.post(
    'core/api/v1/auth/company/add-member',
    emails.map((email) => ({ email }))
  );
  return response.data;
};
